import { useState } from "react";
import {
  Grid,
  Box,
  Link,
  Container,
  Typography,
  Breadcrumbs,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { AdminApi } from "../api/AdminApi";
import { downloadWithURL } from "../helper/ExportHelper";
import { localizedName } from "../helper/LangHelper";

// ----------------------------------------------------------------------

function getCompletenessPercent(completeness) {
  return Math.round(Number(completeness) || 0);
}

function CompletenessPanel({ completeInfo }) {
  if (!completeInfo || completeInfo.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Complete Status
      </Typography>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Percent</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {completeInfo.map((item) => {
              const percent = getCompletenessPercent(item.field_completeness);
              const isComplete = percent === 100;
              return (
                <TableRow key={item.field_name}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {item.field_label}
                  </TableCell>
                  <TableCell align="center">
                    {isComplete ? "✅" : "⏳"}
                  </TableCell>
                  <TableCell align="right">
                    {percent}%
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(percent, 100)}
                      color={isComplete ? "success" : "primary"}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function ResultDataPanel({ resultFields, resultData }) {
  if (!resultFields || resultFields.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Report Data
      </Typography>
      <TableContainer sx={{ overflowX: "auto", maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {resultFields.map((field) => (
                <TableCell
                  key={field.field}
                  sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {field.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(resultData || []).map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {resultFields.map((field) => (
                  <TableCell key={field.field} sx={{ whiteSpace: "nowrap" }}>
                    {row[field.field] ?? ""}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function EventSummaryPage() {
  const { event_id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const event = state?.event;
  const eventName = event
    ? localizedName(event, "name") || event.name_zh
    : "Unknown Event";

  const [reportResult, setReportResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleNavHome = () => {
    navigate("/home");
  };

  const handleNavEvent = () => {
    navigate("/home");
  };

  const onExportHealthSkillReport = () => {
    const eventId = event?.id || event_id;
    setIsLoading(true);
    setReportResult(null);
    AdminApi.exportHealthAndSkillSummary(eventId, (isSuccess, msg, result) => {
      setIsLoading(false);
      if (isSuccess === false) {
        alert(msg);
        return;
      }
      setReportResult(result);
    });
  };

  const onDownloadCsvReport = () => {
    if (!reportResult?.export_url) {
      return;
    }
    downloadWithURL(reportResult.export_url, reportResult.export_filename);
  };

  return (
    <>
      <title>活動總匯資料</title>

      <Container maxWidth="xl">
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleNavHome} sx={{ cursor: "pointer" }}>
            Home
          </Link>

          <Link underline="hover" color="inherit" onClick={handleNavEvent} sx={{ cursor: "pointer" }}>
            {eventName}
          </Link>

          <Typography color="text.primary">Event Summary</Typography>
        </Breadcrumbs>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Typography variant="h3" sx={{ width: { xs: "100%", sm: "auto" } }}>
                  活動總匯資料
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                    onClick={onExportHealthSkillReport}
                    disabled={isLoading}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#4a7bc7",
                      "&:hover": { bgcolor: "#4a7bc7" },
                    }}
                  >
                    下載報表數據
                  </Button>

                </Box>
              </Box>
            </Paper>
          </Grid>

          {reportResult && (
            <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={onDownloadCsvReport}
              sx={{ textTransform: "none" }}
            >
              下載CSV Report
            </Button>
            </Grid>
          )}

          {reportResult && (
            <>
              <Grid item xs={12}>
                <CompletenessPanel completeInfo={reportResult.complete_info} />
              </Grid>
              <Grid item xs={12}>
                <ResultDataPanel
                  resultFields={reportResult.result_fields}
                  resultData={reportResult.result_data}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </>
  );
}
